// /Users/donovan68/dva-frontend-vite/src/pages/shared/PersonalizationMasterPage.tsx
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFilter } from "../../contexts/FilterContext";
import { usePageContext } from "../../contexts/PageContext";
import { useAuth } from "../../contexts/AuthContext";
import { useClientProfile } from "../../hooks/useClientProfile";
import { usePersonalizationSummary } from "../../hooks/usePersonalizationSummary";
import { usePlan } from "../../utils/planUtils";
import { ClientToggle } from "../../components/ClientToggle";
import { HelpIcon } from "../../components/help/HelpIcon";
import { PersonalizationWelcomeCard } from "../../components/personalize/PersonalizationWelcomeCard";
import PersonalizeOverviewCard from "../../components/personalize/Personalizeoverviewcard";
import { PersonalizationSurveyOverview } from "../../components/personalize/PersonalizationSurveyOverview";
import { MessagesContent } from "../../components/MessagesContent";
import { FreemiumCard } from "../../components/FreemiumCard";
import { ProProgressPathV3 } from "../../components/ProProgressPathV3";
import { FreemiumJourneyBarV8 } from "../../components/FreemiumJourneyBarV8";
import FreemiumJourneyHeader from "../../components/freemium/FreemiumJourneyHeader";
import ProJourneyPageHeader from "../../components/journey/pro/ProJourneyPageHeader";

// Import UnifiedSurvey directly instead of UnifiedSurveyPage
import UnifiedSurvey from "../../components/UnifiedSurvey";
import DocumentManagementPage from "../DocumentManagementPage";
import ClientProfilePage from "../ClientProfilePage";

// Icons
import {
  Building,
  ClipboardList,
  FileText,
  ArrowLeft,
  FileSpreadsheet,
  Upload,
  Target,
  RefreshCw,
  AlertCircle,
} from "lucide-react";

type ExpandedView = "profile" | "surveys" | "documents" | null;
type SurveyType = "strategy" | "capabilities" | "readiness" | null;

export default function PersonalizationMasterPage() {
  const navigate = useNavigate();
  const { activeFilter, setActiveFilter } = useFilter();
  const { setPageContext } = usePageContext();
  const [expandedView, setExpandedView] = useState<ExpandedView>(null);
  const [activeSurvey, setActiveSurvey] = useState<SurveyType>(null);

  // ============================================================
  // CLIENT ID: Get from AuthContext session (single source of truth)
  // ============================================================
  const { session, sessionLoading, sessionError } = useAuth();
  const sessionClientId = session?.client_id || null;

  // Loading state - wait for session to load
  if (sessionLoading) {
    return (
      <main className="max-w-[1440px] mx-auto px-6 py-12">
        <div className="flex flex-col items-center justify-center min-h-[400px] text-slate-600">
          <RefreshCw className="w-8 h-8 animate-spin mb-4 text-[#18365E]" />
          <p className="text-lg font-medium">Loading your session...</p>
          <p className="text-sm text-slate-500 mt-1">Please wait while we retrieve your organization data</p>
        </div>
      </main>
    );
  }

  // No client ID - user needs to be associated with an organization
  if (!sessionClientId) {
    return (
      <main className="max-w-[1440px] mx-auto px-6 py-12">
        <div className="flex flex-col items-center justify-center min-h-[400px] text-slate-600">
          <AlertCircle className="w-12 h-12 mb-4 text-amber-500" />
          <h2 className="text-xl font-semibold text-[#18365E] mb-2">No Organization Associated</h2>
          <p className="text-center max-w-md mb-6">
            Your account is not currently associated with an organization. 
            Please contact your administrator or complete onboarding to access personalization features.
          </p>
          <button
            onClick={() => navigate("/home")}
            className="px-6 py-2 bg-[#18365E] text-white rounded-lg hover:bg-[#18365E]/90 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </main>
    );
  }

  // Client override for dev/admin switching (stored in localStorage)
  const [clientOverride, setClientOverride] = useState<string | null>(null);

  // Load client override from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("dva_current_client");
    if (stored) {
      setClientOverride(stored);
    }
  }, []);

  // Effective client: override takes precedence over session client
  const clientId = clientOverride || sessionClientId;
  const { data: clientProfile, userProfile } = useClientProfile(clientId);

  // Get personalization summary with real data
  const { summary, isLoading, error } = usePersonalizationSummary(clientId);

  // Handle client toggle changes
  const handleClientChange = (newClientId: string) => {
    console.log("🏥 Client toggle switching to:", newClientId);
    setClientOverride(newClientId);
    localStorage.setItem("dva_client_override", newClientId);
    setExpandedView(null);
    setActiveSurvey(null);
    window.dispatchEvent(new Event("dva_client_override_changed"));
  };

  // Handle client toggle reset
  const handleResetClient = () => {
    console.log("🏥 Client toggle reset to session client:", sessionClientId);
    setClientOverride(null);
    localStorage.removeItem("dva_client_override");
    setExpandedView(null);
    setActiveSurvey(null);
    window.dispatchEvent(new Event("dva_client_override_changed"));
  };

  // Get plan from entitlement (single source of truth)
  const { plan } = usePlan();
  const isPro = plan === "pro";

  // Debug logging
  console.log("🎨 PersonalizationMasterPage - Rendering");
  console.log(
    "🏥 Client ID:",
    clientId,
    clientOverride ? "(override)" : "(session)",
  );
  console.log("🎨 Plan:", plan);
  console.log("🎨 isPro:", isPro, "expandedView:", expandedView);

  // Journey bar navigation handler
  const handleStepClick = (stepId: number) => {
    if (stepId === 1) navigate("/home");
    else if (stepId === 2) navigate("/personalize");
    else if (isPro) {
      // Pro-specific steps
      if (stepId === 3) navigate("/pro/roi");
      else if (stepId === 4) navigate("/pro/roadmap");
      else if (stepId === 5) navigate("/pro/approval");
      else if (stepId === 6) navigate("/pro/execution");
    } else {
      // Freemium-specific steps
      if (stepId === 3) navigate("/freemium/strategy");
      else if (stepId === 4) navigate("/freemium/upgrade");
    }
  };

  // Set page context with tabs
  useEffect(() => {
    setPageContext({
      breadcrumbs: isPro
        ? [{ label: "Home", href: "/home" }, { label: "Personalize" }]
        : [],
      tabs: ["Tools", "Documents", "Messages"],
    });
  }, [setPageContext, isPro]);

  // Extract data from summary with safe defaults
  const profile = summary?.profile;
  const profileProgress = summary?.profileCompletionPercent ?? 0;
  const surveyProgress = summary?.surveys ?? {
    strategy: { percent: 0 },
    capabilities: { percent: 0 },
    readiness: { percent: 0 },
  };
  const documentsSummary = summary?.documents ?? {
    count: 0,
    processedCount: 0,
  };
  const overallProgress = summary?.overallPercent ?? 0;

  // Calculate average survey progress for welcome card
  const averageSurveyProgress = Math.round(
    ((surveyProgress.strategy.percent ?? 0) +
      (surveyProgress.capabilities.percent ?? 0) +
      (surveyProgress.readiness.percent ?? 0)) /
      3,
  );

  // Personalization data for widget (journey bar integration)
  const personalizationData = {
    strategy: surveyProgress.strategy.percent ?? 0,
    capabilities: surveyProgress.capabilities.percent ?? 0,
    readiness: surveyProgress.readiness.percent ?? 0,
    docsCount: documentsSummary.count ?? 0,
  };

  // Reset expanded view when switching filters to avoid confusing states
  useEffect(() => {
    setExpandedView(null);
    setActiveSurvey(null);
  }, [activeFilter]);

  // Handle survey completion
  const handleSurveyComplete = ({
    clientId: cid,
    surveyType,
  }: {
    clientId: string;
    surveyType: string;
  }) => {
    console.log("✅ Survey completed:", surveyType, "for client:", cid);
    setActiveSurvey(null);
  };

  // Handle survey save and exit
  const handleSurveySaveExit = () => {
    console.log("💾 Survey saved and exited");
    setActiveSurvey(null);
  };

  // Handle generate summary
  const handleGenerateSummary = () => {
    console.log("🎯 Generate Personalization Summary clicked");
    console.log(
      "📊 Profile:",
      profileProgress,
      "% | Surveys:",
      averageSurveyProgress,
      "% | Docs:",
      documentsSummary.count,
    );
    alert(
      "Personalization Summary generation coming soon! This will synthesize your profile, surveys, and documents into a comprehensive strategic overview.",
    );
  };

  // Overview cards configuration
  const cards = useMemo(
    () => [
      {
        key: "profile",
        title: "Organization Profile",
        subtitle: "Your organization's details and characteristics",
        icon: <Building className="w-7 h-7" />,
        progress: profileProgress,
        stats: [
          { label: "Beds", value: profile?.beds ?? "—" },
          { label: "Providers", value: profile?.provider_count ?? "—" },
          {
            label: "Revenue",
            value: profile?.annual_revenue
              ? `$${Number(profile.annual_revenue).toLocaleString()}`
              : "—",
          },
        ],
        ctaLabel: "View / Edit Profile",
        onClick: () => setExpandedView("profile"),
        highlight: profileProgress < 100,
      },
      {
        key: "surveys",
        title: "Strategic Surveys",
        subtitle: "Complete assessments to personalize recommendations",
        icon: <ClipboardList className="w-7 h-7" />,
        sections: [
          { name: "Strategy", progress: surveyProgress.strategy.percent ?? 0 },
          {
            name: "Capabilities",
            progress: surveyProgress.capabilities.percent ?? 0,
          },
          {
            name: "Readiness",
            progress: surveyProgress.readiness.percent ?? 0,
          },
        ],
        ctaLabel: "Continue Surveys",
        onClick: () => {
          setExpandedView("surveys");
          setActiveSurvey(null);
        },
        highlight:
          (surveyProgress.strategy.percent ?? 0) < 100 ||
          (surveyProgress.capabilities.percent ?? 0) < 100 ||
          (surveyProgress.readiness.percent ?? 0) < 100,
      },
      {
        key: "documents",
        title: "Document Library",
        subtitle: "Upload strategic documents to enhance personalization",
        icon: <FileText className="w-7 h-7" />,
        stats: [
          { label: "Uploaded", value: documentsSummary.count },
          { label: "Processed", value: documentsSummary.processedCount },
          { label: "Categories", value: "—" },
        ],
        ctaLabel: "Manage Documents",
        onClick: () => setExpandedView("documents"),
        highlight: documentsSummary.count === 0,
      },
    ],
    [profile, profileProgress, documentsSummary, surveyProgress],
  );

  // Document cards for Documents filter (best practices & guides)
  const documentCards = [
    {
      icon: <FileSpreadsheet className="w-6 h-6" />,
      title: "Survey Guide & Best Practices",
      description: "Get the most out of your strategic assessments.",
      contentType: "list" as const,
      category: "Documents",
      listItems: [
        "How to prepare for surveys",
        "Gathering stakeholder input",
        "Interpreting your results",
        "Updating responses over time",
      ],
      isPremium: false,
      fileType: "PDF",
      fileSize: "2.1 MB",
    },
    {
      icon: <Upload className="w-6 h-6" />,
      title: "Document Upload Guidelines",
      description: "Maximize value from your uploaded strategic documents.",
      contentType: "list" as const,
      category: "Documents",
      listItems: [
        "Supported file formats",
        "Optimal document types",
        "Privacy and security",
        "AI processing explained",
      ],
      isPremium: false,
      fileType: "PDF",
      fileSize: "1.8 MB",
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Profile Optimization Checklist",
      description: "Complete your profile for the best recommendations.",
      contentType: "list" as const,
      category: "Documents",
      listItems: [
        "Essential profile fields",
        "Optional but valuable data",
        "Updating your profile",
        "Data accuracy tips",
      ],
      isPremium: false,
      fileType: "PDF",
      fileSize: "1.5 MB",
    },
  ];

  // Expanded header row (for expanded views)
  const ExpandedHeader = (
    <div className="flex items-center gap-3 mb-3">
      <button
        onClick={() => {
          setExpandedView(null);
          setActiveSurvey(null);
        }}
        className="inline-flex items-center gap-2 text-[13px] text-[#18365E] hover:underline"
        aria-label="Back to Overview"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Overview
      </button>
      <div className="text-[13px] text-slate-500">Home &gt; Personalize</div>
    </div>
  );

  // Tabs Component (reusable)
  const TabsNav = () => (
    <div className="h-10 flex items-center justify-end gap-2 mb-2">
      <nav className="flex items-center gap-2" aria-label="Section navigation">
        {["Tools", "Documents", "Messages"].map((tab) => {
          const active = tab === activeFilter;
          const isMessages = tab === "Messages";
          const showBadge = isMessages && 0 > 0;

          return (
            <button
              key={tab}
              onClick={() => setActiveFilter(tab)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors relative
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#18365E]
                ${active ? "bg-[#18365E] text-white" : "text-[#18365E] hover:bg-black/5"}`}
              aria-current={active ? "page" : undefined}
            >
              {tab}
              {showBadge && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#FF6E4C] text-[10px] font-bold text-white">
                  0
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );

  return (
    <>
      {/* Client Toggle - Always visible */}
      <ClientToggle
        currentClientId={clientId}
        actualClientId={sessionClientId}
        isOverridden={clientOverride !== null}
        onClientChange={handleClientChange}
        onReset={handleResetClient}
      />


      <main className="max-w-[1440px] mx-auto px-6 pb-6">
        {/* NEW LAYOUT ORDER: Header → Journey Bar → Tabs → Content */}

        {/* 1. Page Header - ABOVE journey bar */}
        {!isPro ? (
          <FreemiumJourneyHeader
            stepNumber={2}
            stepName="Personalize"
            description="Capture your organization's profile to tailor recommendations."
          />
        ) : (
          expandedView === null && (
            <div className="mb-6">
              <ProJourneyPageHeader
                stepNumber={2}
                stepName="Personalize"
                description="Complete your profile, surveys, and documents to unlock AI-powered insights tailored to your organization."
              />
            </div>
          )
        )}

        {/* 2. Journey Bar */}
        <div className="mb-6">
          {isPro ? (
            <ProProgressPathV3
              currentStep={2}
              personalizationData={personalizationData}
              onPersonalizationClick={() => navigate("/personalize")}
              onStepClick={handleStepClick}
            />
          ) : (
            <FreemiumJourneyBarV8
              currentStep={2}
              isPersonalized={false}
              personalizationData={personalizationData}
              onPersonalizationClick={() => navigate("/personalize")}
              onStepClick={handleStepClick}
            />
          )}
        </div>

        {/* 3. Tabs - Only show in overview mode for Pro */}
        {isPro && expandedView === null && <TabsNav />}

        {/* 4. Pro Progress Badge - Only in overview mode */}
        {isPro && expandedView === null && (
          <div className="flex justify-end mb-4">
            <div className="inline-flex flex-col items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#18365E] to-[#FF6E4C] shadow-lg">
              <div className="text-[20px] font-bold text-white leading-none">
                {overallProgress}%
              </div>
              <div className="text-[9px] text-white/90 font-medium mt-0.5">
                Complete
              </div>
            </div>
          </div>
        )}

        {/* 5. Content - Conditional based on activeFilter and expandedView */}
        {activeFilter === "Messages" ? (
          <MessagesContent />
        ) : activeFilter === "Documents" && expandedView === null ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documentCards.map((card, idx) => (
              <FreemiumCard
                key={idx}
                icon={card.icon}
                title={card.title}
                description={card.description}
                contentType={card.contentType}
                listItems={card.listItems}
                isPremium={card.isPremium}
              />
            ))}
          </div>
        ) : expandedView === null ? (
          /* Tools view - Overview */
          <>
            {/* Welcome Card - Full Width */}
            <div className="mb-6">
              <PersonalizationWelcomeCard
                profilePercent={profileProgress}
                surveyPercent={averageSurveyProgress}
                documentCount={documentsSummary.count}
                onGenerateSummary={handleGenerateSummary}
                onEditProfile={() => setExpandedView("profile")}
                onContinueSurveys={() => {
                  setExpandedView("surveys");
                  setActiveSurvey(null);
                }}
                onManageDocuments={() => setExpandedView("documents")}
              />
            </div>

            {/* Overview Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {cards.map((c) => (
                <PersonalizeOverviewCard
                  key={c.key}
                  icon={c.icon}
                  title={c.title}
                  subtitle={c.subtitle}
                  progress={(c as any).progress}
                  stats={(c as any).stats}
                  sections={(c as any).sections}
                  ctaLabel={c.ctaLabel}
                  onClick={c.onClick}
                  highlight={(c as any).highlight}
                />
              ))}
            </div>
          </>
        ) : (
          /* Expanded views */
          <div className="rounded-xl bg-white ring-1 ring-slate-200 shadow-sm p-2 md:p-4">
            {ExpandedHeader}

            {expandedView === "profile" && (
              <div className="min-h-[400px]">
                <ClientProfilePage clientId={clientId} />
              </div>
            )}

            {expandedView === "surveys" && activeSurvey === null && (
              <div className="min-h-[400px]">
                <PersonalizationSurveyOverview
                  clientId={clientId}
                  userId={userProfile?.user_id || "admin"}
                  onBackToOverview={() => {
                    setExpandedView(null);
                    setActiveSurvey(null);
                  }}
                  onOpenSurvey={(
                    surveyType: "strategy" | "capabilities" | "readiness",
                  ) => {
                    console.log("🎯 Opening survey:", surveyType);
                    setActiveSurvey(surveyType);
                  }}
                />
              </div>
            )}

            {expandedView === "surveys" && activeSurvey && (
              <div className="min-h-[400px]">
                <UnifiedSurvey
                  isOpen={true}
                  surveyType={activeSurvey}
                  clientId={clientId}
                  userId={userProfile?.user_id || "admin"}
                  userProfile={{
                    client_id: clientId,
                    user_id: userProfile?.user_id || "admin",
                  }}
                  onComplete={handleSurveyComplete}
                  onSaveAndExit={handleSurveySaveExit}
                  allowEdit={true}
                  showReview={true}
                  enableDraftRecovery={true}
                  enableKeyboardNavigation={true}
                />
              </div>
            )}

            {expandedView === "documents" && (
              <div className="min-h-[400px]">
                <DocumentManagementPage clientId={clientId} />
              </div>
            )}
          </div>
        )}
      </main>

      {/* Floating Help Icon - Always Visible */}
      <HelpIcon topicId="personalization_overview" floating />
    </>
  );
}